/* eslint-disable jira/i18n/no-export-define-message */
import React from 'react';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import Flex from '@atlaskit/primitives/components/flex';
import Stack from '@atlaskit/primitives/stack';
import Inline from '@atlaskit/primitives/inline';
import LinkButton from '@atlaskit/button/link';
import Skeleton from '@atlaskit/skeleton';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import PageHeader from '@jira/platform__common-components-page-header/src/index.tsx';

import { useIntl, defineMessages, defineMessage } from '@atlassian/jira-intl';
import { useReturnToListUrl } from './getIsDescriptionEmpty.tsx';

export const messages: {
	returnToList: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	returnToList: {
		id: 'playbooks-edit-screen.playbook-item.skeleton.return-to-list',
		defaultMessage: 'Return to list',
		description: 'Link to return to list of playbooks',
	},
});
export const PlaybookEditSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const [playbookListLink] = useReturnToListUrl();

	const actions = (
		<Flex gap="space.100" direction="row">
			<LinkButton appearance="subtle" href={playbookListLink}>
				{formatMessage(messages.returnToList)}
			</LinkButton>
		</Flex>
	);
	return (
		<Stack xcss={containerStyle} alignBlock="start">
			<PageHeader actions={actions} disableTitleStyles>
				<VisuallyHidden>
					<Heading as="h1" size="small">
						{formatMessage(loadingTitle)}
					</Heading>
				</VisuallyHidden>
			</PageHeader>
			<Flex gap="space.300" direction="row" justifyContent="space-between" xcss={contentStyles}>
				<Stack xcss={stepCards} alignBlock="start" space="space.300">
					<Skeleton height="52px" width="100%" isShimmering borderRadius={token('radius.large')} />
					<Skeleton height="52px" width="100%" isShimmering borderRadius={token('radius.large')} />
					<Inline xcss={addButton} alignInline="center">
						<Skeleton height="40px" width="40px" isShimmering borderRadius={token('radius.full')} />
					</Inline>
				</Stack>
				<Box xcss={detailStyle}>
					<Skeleton height="520px" width="100%" isShimmering borderRadius={token('radius.large')} />
				</Box>
			</Flex>
		</Stack>
	);
};

const containerStyle = xcss({
	backgroundColor: 'elevation.surface.sunken',
	paddingLeft: 'space.300',
	paddingRight: 'space.500',
	height: '100vh',
});

const detailStyle = xcss({
	maxWidth: '800px',
	minWidth: '440px',
	width: '45%',
});

const contentStyles = xcss({
	width: '100%',
	paddingTop: 'space.400',
});

const stepCards = xcss({
	flexGrow: 0,
	flexShrink: 1,
	flexBasis: '30%',
	maxWidth: '600px',
});

const addButton = xcss({
	height: '52px',
});

const loadingTitle = defineMessage({
	id: 'playbooks-edit-screen.playbook-item.skeleton.loading-title',
	defaultMessage: 'Opening playbook ...',
	description: 'Accessible title for the loading skeleton state',
});
